import classNames from "classnames";
import { cloneElement, useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Typography } from "../kit";
import Textbox, { TTextFieldProps } from "../kit/TextField";
import { TFilterOption } from "./Filters";
import SvgIcon from "./SvgIcon";

type FilterFunctionArgsType = {
  values: any;
  isActivated: string[];
  onClear: () => void;
  filters: TFilterOption<any>[];
};

const FilterBadge = ({
  name,
  value,
  onClear,
}: {
  name: string;
  value: ReactNode | string;
  onClear: () => void;
}) => {
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Badge size="xs" state="notice">
        <div className="flex items center gap-x-1">
          <SvgIcon
            onClick={onClear}
            className="cursor-pointer"
            iconName="close"
          />
          <Typography variant="Caption1-Regular" className="!text-gray-400">
            {name}:
          </Typography>
          <Typography variant="Caption1-Regular" className="!text-gray-800">
            {value}
          </Typography>
        </div>
      </Badge>
    </div>
  );
};

const TextboxWithFilter = ({
  className = "",
  filterElement,
  onTextClear,
  value = "",
  onChange,
  ...textBoxProps
}: {
  filterElement?: JSX.Element;
  className?: TTextFieldProps["className"];
  onTextClear?: () => void;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & TTextFieldProps) => {
  const { t } = useTranslation("common");

  // Maintain internal state for the value
  const [internalValue, setInternalValue] = useState(value);

  // Keep internal state in sync with props
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  // Handle text clearing
  const handleClearText = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Update internal state
    setInternalValue("");

    // Create a synthetic change event
    const syntheticEvent = {
      target: { value: "", name: textBoxProps.name },
      currentTarget: { value: "", name: textBoxProps.name },
    } as React.ChangeEvent<HTMLInputElement>;

    // Call onChange with the synthetic event
    if (onChange) {
      onChange(syntheticEvent);
    }

    // Call onTextClear if provided
    if (onTextClear) {
      onTextClear();
    }
  };

  // Create a combined text input adornment with search icon
  const startAdornment = (
    <div className="flex items-center">
      <SvgIcon className="w-5 h-5" iconName="search2" fill="fill-unset" />
    </div>
  );

  // Create a close button that appears only when there's text in the input
  const textClearAdornment = internalValue ? (
    <div className="flex items-center mr-2">
      <SvgIcon
        className="w-4 h-4 cursor-pointer text-gray-400 hover:text-gray-600"
        iconName="close"
        onClick={handleClearText}
      />
    </div>
  ) : null;

  const activeFiltersRenderer = (
    values: { name: string; value: ReactNode | string; onClear: () => void }[],
  ) => {
    const { t } = useTranslation("common");
    const [first, ...rest] = values;

    if (values.length > 2) {
      return (
        <div className="flex items-center gap-x-2">
          <FilterBadge
            onClear={first?.onClear}
            name={first.name}
            value={first.value}
          />
          <Badge className="capitalize" size="xs" state="notice">
            <div className="flex items-center">
              & {rest.length} {t("more")}
              <SvgIcon className="w-5 h-5 rotate-90" iconName="arrowRight" />
            </div>
          </Badge>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-x-2">
        {values.map((item) => (
          <FilterBadge
            onClear={item.onClear}
            name={item.name}
            value={item.value}
            key={item.name}
          />
        ))}
      </div>
    );
  };

  // Combine all adornments based on your Textbox component's structure
  function combinedEndAdornment() {
    if (!filterElement) return <></>;
    return (
      <>
        {cloneElement(filterElement, {
          filteredElement: ({
            isActivated,
            onClear,
            values,
            filters,
          }: FilterFunctionArgsType) => {
            const activeValues = Object.keys(values).filter(
              (item) => !!values[item],
            );
            const activefilters = filters.filter((item) =>
              activeValues.includes(item.name.toString()),
            );
            const payloadFilters = activefilters.map((item) => ({
              name: item?.label || "",
              value: item?.dataToShow || "",
              onClear: item?.onFieldClear || function () {},
            }));

            return (
              <div className="flex items-center cursor-pointer rounded-[6px] h-[32px] py-2">
                <div className="flex gap-2 items-center">
                  {!isActivated.length && (
                    <Typography variant="Body2-Regular" color="text-gray-400">
                      {t("filter")}
                    </Typography>
                  )}
                  {activeFiltersRenderer(payloadFilters)}

                  {!!payloadFilters.length && (
                    <SvgIcon
                      iconName="trash"
                      className="w-5 h-5 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                      }}
                    />
                  )}
                  <div className="relative">
                    <SvgIcon iconName="filter" />
                    {!!payloadFilters.length && (
                      <div className="absolute w-[6px] h-[6px] rounded-full bg-red-500 -top-[2px] -right-[2px]" />
                    )}
                  </div>
                </div>
                <div className="flex-1" />
              </div>
            );
          },
        })}
      </>
    );
  }

  return (
    <Textbox
      startAdornment={startAdornment}
      endAdornment={
        <>
          {textClearAdornment}
          {combinedEndAdornment()}
        </>
      }
      value={internalValue}
      onChange={handleChange}
      className={classNames("!h-10 !bg-gray-50 !w-full", className)}
      {...textBoxProps}
    />
  );
};

export default TextboxWithFilter;
